import { useQuery } from '@apollo/client/react';
import React from 'react';
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  Create,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextInput,
} from 'react-admin';
import { ExerciseUnitsDocument, Prefix } from '../../../graphql/types';
import FrameCapturePreview from '../components/FrameCapturePreview';
import ImageUploadInput from '../components/ImageUploadInput';
import VideoUploadInput from '../components/VideoUploadInput';

export const ExerciseCreate: React.FC = () => {
  const [capturedFrame, setCapturedFrame] = React.useState<string | null>(null);

  const { data: exerciseUnitsData, loading: defaultUnitsLoading } = useQuery(
    ExerciseUnitsDocument,
  );
  const defaultUnits = (exerciseUnitsData?.exerciseUnits || []).map((unit) => ({
    id: unit.value,
    name: unit.name,
  }));

  const handleFrameCapture = React.useCallback((dataUrl: string) => {
    setCapturedFrame(dataUrl);
  }, []);

  const handleReplaceThumbnail = React.useCallback(() => {
    setCapturedFrame(null);
  }, []);

  const handleClearFrame = React.useCallback(() => {
    setCapturedFrame(null);
  }, []);

  return (
    <Create>
      <SimpleForm>
        <ReferenceInput source="exerciseBase.id" reference="ExerciseBase">
          <AutocompleteInput
            optionText="name"
            filterToQuery={(name: string) => ({ name })}
            label="Base Movement"
            fullWidth
          />
        </ReferenceInput>
        <TextInput source="name" label="Name" validate={required()} fullWidth />
        <TextInput
          source="displayName"
          fullWidth
          helperText='This will override "Name" in the app. If empty, "Name" will be used.'
          sx={{ marginBottom: 4 }}
        />
        <ArrayInput
          source="coachingKeys"
          label="Instructions"
          aria-label="Instructions"
        >
          <SimpleFormIterator>
            <TextInput
              source=""
              fullWidth
              label="Instructions"
              style={{ width: '50vw' }}
            />
          </SimpleFormIterator>
        </ArrayInput>
        <TextInput
          source="modifications"
          fullWidth
          multiline
          label="Modifications"
        />
        <TextInput
          source="coachNotes"
          fullWidth
          multiline
          label="Coach Notes"
        />
        <SelectInput
          isPending={defaultUnitsLoading}
          source="defaultUnit.value"
          label="Default Unit"
          choices={defaultUnits}
        />
        <BooleanInput
          source="canTrackWeight"
          label="Can Track Weight"
          defaultValue={false}
        />
        <NumberInput
          source="progression"
          label="Progression"
          step={1}
          helperText="Optional exercise-level progression (integer)."
        />
        <ArrayInput source="bodyParts" label="Body parts">
          <SimpleFormIterator>
            <TextInput source="" fullWidth label="Body part" />
          </SimpleFormIterator>
        </ArrayInput>
        <ArrayInput source="equipment" label="Equipment">
          <SimpleFormIterator>
            <TextInput source="" fullWidth label="Equipment" />
          </SimpleFormIterator>
        </ArrayInput>
        <ArrayInput source="oneRepMax" label="One rep max (kg)">
          <SimpleFormIterator>
            <NumberInput source="" label="Weight (kg)" />
          </SimpleFormIterator>
        </ArrayInput>
        <VideoUploadInput
          placeholder="Drop a video file to upload, or click to select it."
          multiple={false}
          prefix={Prefix.ExerciseVideo}
          source="video"
          onFrameCapture={handleFrameCapture}
        />
        <FrameCapturePreview
          capturedFrame={capturedFrame}
          onReplaceThumbnail={handleReplaceThumbnail}
          onClearFrame={handleClearFrame}
          thumbnailSource="thumbnail"
        />
        <ImageUploadInput
          placeholder="Drop an image file to upload, or click to select it."
          multiple={false}
          prefix={Prefix.ExerciseThumbnail}
          source="thumbnail"
        />
      </SimpleForm>
    </Create>
  );
};
